import { useCallback, useEffect, useRef, useState } from "react";

import { LISBlock, LISSpecimen } from "pwapi";

import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { isBisected } from "shared/utils/specimenUtils";

import { useAppSelector } from "app/hooks";
import {
  isMultipleExcisions,
  isMultiplePunches,
  isMultiplePunchesInSingleCassette,
  isMultipleShaves,
  isMultipleShavesInSingleCassette,
  isExcisionWithTips,
  isShaveFragments,
  isNail,
} from "util/grossingMadLibs/conditions";
import {
  GrossingModifierSelections,
  GrossingTokenSelections,
} from "util/grossingMadLibs/selections";

const useGrossingBlockDetails = ({
  tokenSelections,
  tokenModifiers,
  specimen,
  isRawText,
}: {
  tokenSelections: GrossingTokenSelections;
  tokenModifiers: GrossingModifierSelections;
  specimen: LISSpecimen;
  isRawText: boolean;
}) => {
  const showPieceCount = useAppSelector((state) =>
    selectFeatureFlag(state, "lisGrossPieceCount")
  );
  const prevTokenSelections = useRef<GrossingTokenSelections | undefined>(
    undefined
  );
  const prevModifierSelections = useRef<GrossingModifierSelections | undefined>(
    undefined
  );

  const [blockDetails, setBlockDetails] = useState<Partial<LISBlock>[]>([]);
  const [isManual, setIsManual] = useState<boolean>(false);

  const isTokenSelectionChanged = useCallback(
    (tokensList: string[]) => {
      // The component hasn't been initialized, so theres nothing to compare
      if (!prevTokenSelections) return true;
      return !!tokensList.some(
        (token) =>
          tokenSelections?.[token]?.value !==
          prevTokenSelections.current?.[token]?.value
      );
    },
    [tokenSelections, prevTokenSelections]
  );

  const updateManualBlockDetails = useCallback(() => {
    if (
      !prevTokenSelections ||
      tokenSelections?.cassette_count?.value !==
        prevTokenSelections.current?.cassette_count?.value
    ) {
      const currCassetteCount = parseInt(
        tokenSelections?.cassette_count?.value || "0"
      );
      // Get the difference in the cassette counts to adjust the number of visible blocks available
      const diff =
        currCassetteCount -
        parseInt(prevTokenSelections.current?.cassette_count?.value || "0");
      if (!prevTokenSelections?.current?.cassette_count?.value) {
        setBlockDetails(Array(currCassetteCount).fill({ piece_count: null }));
      }
      // A block was removed after manually adjusting other block details
      else if (diff < 0) {
        setBlockDetails(blockDetails.slice(0, diff));
      }
      // A block was added after manually adjusting the other block details
      else {
        setBlockDetails([
          ...blockDetails,
          ...Array(diff).fill({ piece_count: null }),
        ]);
      }
      prevTokenSelections.current = tokenSelections;
    }
  }, [tokenSelections, prevTokenSelections]);

  const getGrossedBlockDetails = useCallback(() => {
    const currCassetteCount = parseInt(
      tokenSelections?.cassette_count?.value || "0"
    );

    // Setup object for the various methods such as isMultipleShaves or isExcisionWithTips
    const tokenDetails = {
      tokenSelections,
      modifierSelections: tokenModifiers,
      specimen,
    };

    // No default can be determined for multiple punches/shaves within a single cassette
    if (
      isMultipleShavesInSingleCassette(tokenDetails) ||
      isMultiplePunchesInSingleCassette(tokenDetails)
    ) {
      setBlockDetails([{ piece_count: null }]);

      // No default can be determined for a submission which is representative
      // No default can be determined for multiple shaves which are fragmented
      // Currently excision tips are not supported by the default piece count system
    } else if (
      (tokenSelections?.submission?.value === "representatively" ||
        isExcisionWithTips(tokenDetails) ||
        isShaveFragments(tokenDetails)) &&
      (!isRawText || isTokenSelectionChanged(["cassette_count", "submission"]))
    ) {
      setBlockDetails(Array(currCassetteCount).fill({ piece_count: null }));

      // Support for the standard procedure for grossing without any multiple shaves
    } else if (
      !isRawText ||
      isTokenSelectionChanged(["cassette_count", "grossed"])
    ) {
      // If the grossed value is undefined or blank, only give defaults if there is 1 cassette
      if (
        !tokenSelections?.grossed?.value &&
        tokenSelections?.cassette_count?.value === LISBlock.piece_count._1
      ) {
        // For nail protocols, the piece count defaults are based on the clipping count instead of the cassette count
        const standardPieceCount = isNail(tokenDetails)
          ? tokenSelections.clipping_count?.value
          : tokenSelections?.cassette_count?.value;

        setBlockDetails([
          {
            piece_count: standardPieceCount as
              | LISBlock.piece_count
              | null
              | undefined,
          },
        ]);
      }

      // If the grossed value is bisected, only give defaults if there is 1 or 2 cassettes
      else if (
        isBisected(tokenSelections?.grossed?.value) &&
        (tokenSelections?.cassette_count?.value === LISBlock.piece_count._2 ||
          tokenSelections?.cassette_count?.value === LISBlock.piece_count._1)
      ) {
        setBlockDetails(
          Array(currCassetteCount).fill({
            piece_count:
              tokenSelections?.cassette_count?.value === LISBlock.piece_count._2
                ? LISBlock.piece_count._1
                : LISBlock.piece_count._2,
          })
        );

        // If the grossed value is trisected, only give defaults if there is 1 or 3 cassettes
      } else if (
        tokenSelections?.grossed?.value === "trisected" &&
        (tokenSelections?.cassette_count?.value === LISBlock.piece_count._3 ||
          tokenSelections?.cassette_count?.value === LISBlock.piece_count._1)
      ) {
        setBlockDetails(
          Array(currCassetteCount).fill({
            piece_count:
              tokenSelections?.cassette_count?.value === LISBlock.piece_count._3
                ? LISBlock.piece_count._1
                : LISBlock.piece_count._3,
          })
        );

        // The grossed value does not match the number of cassettes and requires manual intervention
      } else {
        setBlockDetails(Array(currCassetteCount).fill({ piece_count: null }));
      }
    }
    prevTokenSelections.current = tokenSelections;
    prevModifierSelections.current = tokenModifiers;
  }, [
    tokenSelections,
    tokenModifiers,
    prevTokenSelections,
    prevModifierSelections,
    isRawText,
  ]);

  const getMultipleGrossedBlockDetails = useCallback(() => {
    const currCassetteCount = parseInt(
      tokenSelections?.cassette_count?.value || "0"
    );

    // If the cassette count is greater than the type count, then don't set any gross piece count defaults, as the user can now select a range of cassettes for each shave.
    if (
      tokenSelections.cassette_count?.value &&
      tokenSelections.type_count?.value &&
      parseInt(tokenSelections.cassette_count?.value) >
        parseInt(tokenSelections.type_count?.value)
    ) {
      setBlockDetails(Array(currCassetteCount).fill({ piece_count: null }));
      return;
    }

    // The cassette count is too low for multiple cassettes and shouldn't be shown until a proper cassette count is chosen
    if (currCassetteCount < 2) {
      setBlockDetails([]);
      return;
    }

    let isChanged = false;

    const updatedBlockDetails = Array(currCassetteCount)
      .fill({})
      .map((_, idx) => {
        const tokenSuffix = idx === 0 ? "" : `_${idx + 1}`;
        // The grossed token associated with the block at the specific index
        const grossedToken = tokenSelections?.[`grossed${tokenSuffix}`]?.value;
        // The submission token associated with the block at the specific index
        const submissionToken =
          tokenSelections?.[`submission${tokenSuffix}`]?.value;

        // Compare if there was any change to either token per block per index. If there was a change, update the details and then set isChanged to true
        // Otherwise simply return the old block details as they haven't changed
        if (
          !isRawText ||
          isTokenSelectionChanged([
            "cassette_count",
            `type_cassette_count${tokenSuffix}`,
            `grossed${tokenSuffix}`,
            `submission${tokenSuffix}`,
          ])
        ) {
          isChanged = true;
          return {
            piece_count:
              submissionToken === "entirely"
                ? !grossedToken
                  ? LISBlock.piece_count._1
                  : isBisected(grossedToken)
                  ? LISBlock.piece_count._2
                  : grossedToken === "trisected"
                  ? LISBlock.piece_count._3
                  : null
                : null,
          };
        } else {
          return blockDetails[idx];
        }
      });

    // If any of the block details have been changed, update the state
    // Otherwise theres no need to update the state or refs and so don't do anything.
    if (isChanged) {
      prevTokenSelections.current = tokenSelections;
      setBlockDetails(updatedBlockDetails);
    }
  }, [tokenSelections, prevTokenSelections, isRawText]);

  useEffect(() => {
    // if tokenSelections.cassette_count changes to undefined, set the previous token selections.cassette_count to undefined as well to prevent stale state issues if the user goes back and forth between a defined cassette count and an undefined cassette count. This can happen when resetting all tokens at once
    if (
      prevTokenSelections.current?.cassette_count?.value &&
      !tokenSelections?.cassette_count?.value
    ) {
      const { cassette_count: _cassette_count, ...rest } =
        prevTokenSelections.current!;
      prevTokenSelections.current = { ...rest };
    }
  }, [tokenSelections?.cassette_count]);

  useEffect(() => {
    if (!showPieceCount) return;
    if (isManual || isRawText) {
      updateManualBlockDetails();
    } else if (
      (isMultipleShaves({
        tokenSelections,
        modifierSelections: tokenModifiers,
        specimen,
      }) ||
        isMultipleExcisions({
          tokenSelections,
          modifierSelections: tokenModifiers,
          specimen,
        }) ||
        isMultiplePunches({
          tokenSelections,
          modifierSelections: tokenModifiers,
          specimen,
        })) &&
      !tokenModifiers.singleCassette &&
      !tokenModifiers.multipleFragments
    ) {
      getMultipleGrossedBlockDetails();
    } else {
      getGrossedBlockDetails();
    }
  }, [isManual, tokenSelections, tokenModifiers, isRawText]);

  const manuallySetPieceCount = (idx: number, details: Partial<LISBlock>) => {
    setBlockDetails([
      ...blockDetails.slice(0, idx),
      details,
      ...blockDetails.slice(idx + 1),
    ]);
    setIsManual(true);
  };

  return {
    blockDetails,
    setIsManual,
    manuallySetPieceCount,
  };
};

export default useGrossingBlockDetails;
